import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, CheckCircle, Clock } from "lucide-react";
import dreamWorldProvider from "../DreamWorldProvider";
import { useSelector, useDispatch } from "react-redux";
import {
  selectUserGlobalData,
  updateUserData,
} from "../store/dreamworldSlice";

interface GiftModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasGift: boolean;
  setHasGift: (hasGift: boolean) => void;
}

const GiftModal: React.FC<GiftModalProps> = ({
  isOpen,
  onClose,
  hasGift,
  setHasGift,
}) => {
  const [isClaimed, setIsClaimed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");
  const userGlobalData = useSelector(selectUserGlobalData);

  const dispatch = useDispatch();

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const lastClaimedTimestamp =
        userGlobalData?.userData?.lastClaimedTimestamp;
      if (lastClaimedTimestamp) {
        const lastClaimedDate = lastClaimedTimestamp.toDate();
        const now = new Date();
        const diffInMillis = now.getTime() - lastClaimedDate.getTime();
        const diffInHours = diffInMillis / (1000 * 60 * 60);

        if (diffInHours < 24) {
          const hours = Math.floor(23 - diffInHours);
          const minutes = Math.floor(59 - ((diffInHours * 60) % 60));
          const seconds = Math.floor(59 - ((diffInMillis / 1000) % 60));
          setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
          setHasGift(false);
        } else {
          setHasGift(true);
        }
      } else {
        setHasGift(true);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [userGlobalData, setHasGift]);

  const handleClaimGift = async () => {
    if (userGlobalData && userGlobalData.userData) {
      try {
        const updatedUserData = await dreamWorldProvider.claimDailyGift(
          userGlobalData.userData.uid
        );
        dispatch(updateUserData(updatedUserData));
        setIsClaimed(true);
      } catch (error) {
        console.error("Error claiming gift:", error);
        // Optionally, show an error message to the user
      }
    }
  };

  const renderGiftAvailable = () => (
    <div className="p-8 text-center">
      <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 p-3 rounded-full mb-6">
        <Gift className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-4xl font-bold text-white mb-4">You have a Gift!</h2>
      <p className="text-gray-300 mb-8">
        You've received 10 Flux credits as a daily gift!
      </p>
      <button
        onClick={handleClaimGift}
        className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-bold py-4 rounded-xl text-lg hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
      >
        Claim Gift
      </button>
    </div>
  );

  const renderGiftClaimed = () => (
    <div className="p-8 text-center">
      <div className="inline-block bg-gradient-to-r from-green-400 to-blue-500 p-3 rounded-full mb-6">
        <CheckCircle className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-4xl font-bold text-white mb-4">Success!</h2>
      <p className="text-gray-300 mb-8">
        You've claimed your 10 Flux credits. Check back in 24 hours for a new
        gift!
      </p>
      <button
        onClick={onClose}
        className="w-full bg-gray-700 text-white font-bold py-4 rounded-xl text-lg hover:bg-gray-600 transition-all duration-300"
      >
        Close
      </button>
    </div>
  );

  const renderNoGift = () => (
    <div className="p-8 text-center">
      <div className="inline-block bg-gradient-to-r from-gray-600 to-gray-800 p-3 rounded-full mb-6">
        <Clock className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-4xl font-bold text-white mb-4">No Gift Yet</h2>
      <p className="text-gray-300 mb-8">
        You've already claimed your gift for today. Check back later!
      </p>
      <p className="text-2xl font-bold text-yellow-400">{timeRemaining}</p>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 rounded-2xl max-w-lg w-full overflow-hidden border-2 border-purple-500 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {hasGift
              ? isClaimed
                ? renderGiftClaimed()
                : renderGiftAvailable()
              : renderNoGift()}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GiftModal;
